import { v4 as uuidv4 } from 'uuid';
import { getServices } from '~/db/services';
import DialogNotASubscriber from '~/components/categories/assessment/dialog/DialogNotASubscriber.vue';

type DisciplineInfos = {
  id: string;
  slug: string;
  title: string;
};

type ChapterInfos = {
  id: string;
  title: string;
};

export type UseAssessmentForm = {
  currentStep: Ref<{
    step: AssessmentFormSteps;
    type: AssessmentFormSelectorTypes | null;
  }>;
  currentContent: Ref<UserContent>;
  chapters: ComputedRef<ChapterInfos[]>;
  chapterIds: ComputedRef<string[]>;
  currentDiscipline: ComputedRef<DisciplineInfos>;
  dateTime: Ref<string>;
  dateIso: ComputedRef<string>;
  toggleChapter: (id: string, title: string) => void;
  setDiscipline: (id: string, slug: string, title: string) => void;
  back: () => void;
  next: () => void;
  setStep: (
    step: AssessmentFormSteps,
    type: AssessmentFormSelectorTypes | null
  ) => void;
  setDate: (dateTime: string) => void;
  uuid: Ref<string>;
  saveEnabled: ComputedRef<boolean>;
  save: () => Promise<void>;
};

export const INJECT_ASSESSMENT_FORM = Symbol(
  'useAssessmentForm composable'
) as InjectionKey<UseAssessmentForm>;

export const useAssessmentForm = (): UseAssessmentForm => {
  const { $db } = useNuxtApp();
  const { assessmentsService } = getServices($db);

  const { mainContent } = useContents();

  const profile = useProfileStore();

  const dialog = useDialogStore();

  const currentStep = ref<{
    step: AssessmentFormSteps;
    type: AssessmentFormSelectorTypes | null;
  }>({
    step: AssessmentFormSteps.SELECT_CHAPTERS,
    type: AssessmentFormSelectorTypes.Disciplines,
  });

  const uuid = ref<string>(uuidv4().toUpperCase());

  const context = useContextStore();

  const historyDisciplines = ref<DisciplineInfos[]>([]);

  const mChapters = ref<Map<string, ChapterInfos>>();
  mChapters.value = new Map();

  const dateTime = ref<string>();

  const dateIso = computed(() => {
    return new Date(dateTime.value).toISOString();
  });

  const setDiscipline = (id: string, slug: string, title: string) => {
    historyDisciplines.value.unshift({
      id,
      slug,
      title,
    });
  };

  const currentDiscipline = computed(() => {
    if (historyDisciplines.value.length === 0) return null;
    return historyDisciplines.value[0];
  });

  const toggleChapter = (id: string, title: string) => {
    if (mChapters.value.has(id)) {
      mChapters.value.delete(id);
    } else {
      mChapters.value.set(id, { title, id });
    }
  };

  const chapters = computed(() => {
    return Array.from(mChapters.value.values());
  });

  const chapterIds = computed(() => {
    return chapters.value.map((ch) => ch.id);
  });

  const setDate = (strDateTime: string) => {
    dateTime.value = `${strDateTime}`;
  };

  const setStep = (
    step: AssessmentFormSteps,
    type: AssessmentFormSelectorTypes | null
  ) => {
    currentStep.value = {
      step,
      type,
    };
  };

  const back = () => {
    if (currentStep.value.step === AssessmentFormSteps.SELECT_CHAPTERS) {
      historyDisciplines.value.shift();
    } else if (currentStep.value.step === AssessmentFormSteps.SELECT_DATE) {
      dateTime.value = null;
      currentStep.value.step = AssessmentFormSteps.SELECT_CHAPTERS;
    }
  };

  const next = () => {
    currentStep.value = {
      ...currentStep.value,
      step: AssessmentFormSteps.SELECT_DATE,
    };
  };

  watch(
    () => currentDiscipline.value?.slug,
    (to, from) => {
      if (from !== to) {
        mChapters.value = new Map([]);
      }
    }
  );

  const showDialogNotAllowedToCreateAssessment = () => {
    dialog.open(DialogNotASubscriber, {
      position: 'center',
      level: DialogLevel.Info,
      onClose: () => {
        navigateTo(`/${Sections.ChildForYou}`);
      },
    });
  };

  const save = async () => {
    if (!profile.canCreateNewAssessment) {
      return showDialogNotAllowedToCreateAssessment();
    }
    const data = {
      member: toRaw(context.memberId),
      uuid: toRaw(uuid.value).toUpperCase(),
      chapterIds: toRaw(chapterIds.value),
      id: '',
      date: toRaw(dateTime.value),
      libraryBookId: toRaw(mainContent.value.id),
      disciplineId: toRaw(currentDiscipline.value.id),
    };

    try {
      await assessmentsService.put(data);
      await useAssessmentsStore().loadFromClient();
      requestAnimationFrame(() => {
        currentStep.value = {
          ...currentStep.value,
          step: AssessmentFormSteps.COMPLETED,
        };
      });
    } catch (e) {
      globalThis.$logger.info(
        'saveNewAssessment ::: unable to save new assessment',
        e
      );
    }
  };

  const saveEnabled = computed<boolean>(() => {
    return Boolean(
      currentStep.value.step === AssessmentFormSteps.SELECT_DATE &&
        dateTime.value
    );
  });

  return {
    currentStep,
    currentContent: mainContent,
    currentDiscipline,
    chapters,
    chapterIds,
    dateTime,
    dateIso,
    toggleChapter,
    setDiscipline,
    back,
    next,
    setStep,
    setDate,
    uuid,
    saveEnabled,
    save,
  };
};
